import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FaUserCircle,
  FaQrcode,
  FaTimes,
  FaCamera,
  FaDownload,
  FaCopy,
  FaShareAlt,
  FaEye,
  FaEyeSlash,
  FaIdBadge,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaCheck,
  FaPalette,
} from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import UserAvatar from "../components/UserAvatar";
import { createPortal } from "react-dom";
import { useTheme } from "../theme/ThemeProvider";
import { THEMES } from "../theme/themeConfig";
import { authFetch } from "../utils/api";

/* ----------------------------- Toast (minimal) ----------------------------- */
const Toast = ({ kind = "info", message, onClose }) => {
  const bg =
    kind === "success" ? "var(--success)" : kind === "error" ? "var(--error)" : "var(--accent)";
  return (
    <div
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: 20,
        zIndex: 999999,
        background: bg,
        color: "#fff",
        padding: "10px 18px",
        borderRadius: "var(--radius-md)",
        boxShadow: "0 16px 40px -18px rgba(0,0,0,.6)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        backdropFilter: "blur(8px)",
      }}
      role="status"
      aria-live="polite"
    >
      <span style={{ fontSize: 13 }}>{message}</span>
      <button
        onClick={onClose}
        className="icon-btn"
        style={{ width: 20, height: 20, color: "#fff" }}
        aria-label="Dismiss"
      >
        <FaTimes size={11} />
      </button>
    </div>
  );
};

/* --------------------------------- Page ---------------------------------- */
export default function MyProfile() {
  /* -------------------------- Load user (local) -------------------------- */
  const initialUser = JSON.parse(localStorage.getItem("user")) || {
    name: "Unknown User",
    role: "Member",
    address: "—",
    contactno: "—",
    image: "/user.png",
    SLIITID: "",
  };
  const [user, setUser] = useState(initialUser);

  /* ------------------------------ UI helpers ----------------------------- */
  const [toast, setToast] = useState(null);
  const [toastKind, setToastKind] = useState("info");
  const showToast = (msg, kind = "info", ms = 1600) => {
    setToastKind(kind);
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  };

  /* ------------------------------ Appearance ------------------------------ */
  const { theme, setTheme } = useTheme();
  const handleSelectTheme = (id) => {
    if (id === theme) return;
    setTheme(id);
    const picked = THEMES.find((t) => t.id === id);
    showToast(`Theme applied successfully — ${picked?.name || ""}`, "success", 2000);
  };

  /* ------------------------------ Profile form --------------------------- */
  const [profile, setProfile] = useState({
    name: initialUser.name || "",
    Address: initialUser.address || "",
    ContactNo: initialUser.contactno || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  /* ------------------------------- QR Modal ------------------------------ */
  const [showQRModal, setShowQRModal] = useState(false);
  const qrBoxRef = useRef(null);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setShowQRModal(false);
    if (showQRModal) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onEsc);
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [showQRModal]);

  const downloadQR = useCallback(() => {
    if (!qrBoxRef.current) return;
    const svg = qrBoxRef.current.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const dataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
    const img = new Image();
    const size = 720;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0b1120";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png;
      a.download = `${user.SLIITID || "member"}-qr.png`;
      a.click();
    };
    img.src = dataUrl;
  }, [user.SLIITID]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(user.SLIITID || "");
      showToast("ID copied", "success", 1200);
    } catch {
      showToast("Copy failed", "error");
    }
  };

  const shareQR = async () => {
    const text = `Member: ${user.name || "User"}\nID: ${user.SLIITID || "N/A"}\nRole: ${user.role || "Member"}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Member QR", text });
      } catch {
        /* cancelled */
      }
    } else {
      showToast("Share not supported on this device");
    }
  };

  /* ----------------------------- Image Upload ---------------------------- */
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const openFilePicker = () => fileInputRef.current?.click();

  const onFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) return showToast("Max file size is 5MB", "error");
    setSelectedImage(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
  };

  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const handleImageUpload = async () => {
    if (!selectedImage) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("image", selectedImage);
      if (user?.SLIITID) fd.append("SLIITID", user.SLIITID);

      const res = await authFetch("https://dev.sliitleo.org/Backend/upload_profile_image.php", {
        method: "POST",
        body: fd,
      });

      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const out = await res.json().catch(() => null);
      if (!out?.success || !out?.url) throw new Error(out?.message || "Upload failed");

      const publicUrl = out.url.startsWith("http") ? out.url : `https://dev.sliitleo.org${out.url}`;

      // Persist (best effort)
      await authFetch("https://dev.sliitleo.org/Backend/update_profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          SLIITID: user?.SLIITID,
          Image: publicUrl,
          FullName: user?.name ?? "",
          Address: user?.address ?? "",
          ContactNo: user?.contactno ?? "",
        }),
      }).catch(() => {});

      const next = { ...user, image: publicUrl };
      localStorage.setItem("user", JSON.stringify(next));
      setUser(next);
      setSelectedImage(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      showToast("Photo updated", "success");
    } catch (e) {
      showToast(e.message || "Upload error", "error");
    } finally {
      setUploading(false);
    }
  };

  /* --------------------------- Password handling ------------------------- */
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const checks = {
    len: passwordData.newPassword.length >= 8,
    uc: /[A-Z]/.test(passwordData.newPassword),
    num: /\d/.test(passwordData.newPassword),
    sym: /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword),
    match:
      passwordData.newPassword.length > 0 &&
      passwordData.newPassword === passwordData.confirmPassword,
  };
  const strong = checks.len && checks.uc && checks.num && checks.sym;

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((p) => ({ ...p, [name]: value }));
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!strong || !checks.match) {
      return setPasswordError("Please meet all requirements and confirm the same password.");
    }
    try {
      setIsUpdating(true);
      const apiUrl = new URL("https://dev.sliitleo.org/Backend/change_password.php");
      apiUrl.searchParams.append("_", Date.now());

      const rsp = await authFetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          // intentionally no X-Requested-With (avoids CORS block)
        },
        body: JSON.stringify({
          SLIITID: user?.SLIITID,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const result = await rsp.json().catch(() => ({}));
      if (result?.success) {
        setPasswordSuccess("Password updated");
        setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        throw new Error(result?.message || "Update failed");
      }
    } catch (err) {
      setPasswordError(err.message || "Unknown error");
    } finally {
      setIsUpdating(false);
    }
  };

  /* --------------------------- Load profile info -------------------------- */
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user"));
    if (!u?.SLIITID) return;
    setLoadingProfile(true);
    fetch("https://dev.sliitleo.org/Backend/get_profile.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ SLIITID: u.SLIITID }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d?.success && d.profile) {
          setProfile({
            name: d.profile.name || u.name || "",
            Address: d.profile.Address || u.address || "",
            ContactNo: d.profile.ContactNo || u.contactno || "",
          });
          if (d.profile.image) {
            const next = { ...u, image: d.profile.image };
            localStorage.setItem("user", JSON.stringify(next));
            setUser(next);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, []);

  const saveProfile = async () => {
    try {
      setSavingProfile(true);
      // Persist to backend (best effort)
      await authFetch("https://dev.sliitleo.org/Backend/update_profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          SLIITID: user?.SLIITID,
          FullName: profile.name,
          Address: profile.Address,
          ContactNo: profile.ContactNo,
          Image: user?.image || "",
        }),
      }).catch(() => {});
      const nextLocal = {
        ...user,
        name: profile.name,
        address: profile.Address,
        contactno: profile.ContactNo,
      };
      localStorage.setItem("user", JSON.stringify(nextLocal));
      setUser(nextLocal);
      showToast("Profile saved", "success");
    } catch {
      showToast("Unable to save", "error");
    } finally {
      setSavingProfile(false);
    }
  };

  /* ---------------------------------- UI --------------------------------- */
  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    padding: "8px 12px",
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-3 sm:px-6 md:px-10 py-6 md:py-10 relative">
        {/* Ambient glows */}
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
          <div
            style={{
              position: "absolute",
              top: -96,
              left: -96,
              width: 320,
              height: 320,
              borderRadius: "50%",
              background: "var(--glow-1, rgba(124,58,237,0.14))",
              filter: "blur(90px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: -96,
              right: -96,
              width: 320,
              height: 320,
              borderRadius: "50%",
              background: "var(--glow-2, rgba(47,111,237,0.12))",
              filter: "blur(90px)",
            }}
          />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="page-head"
          style={{ maxWidth: 1080, margin: "0 auto var(--sp-6)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-3)" }}>
            <span
              style={{
                display: "inline-grid",
                placeItems: "center",
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "var(--accent)",
                color: "#fff",
                flexShrink: 0,
                boxShadow: "0 10px 24px -12px rgba(124,58,237,.5)",
              }}
            >
              <FaUserCircle size={18} />
            </span>
            <div>
              <div className="eyebrow">Account</div>
              <h1>My Profile</h1>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            <span className="pill pill-info">
              <span className="dot" />
              ID <span className="num">{user.SLIITID || "N/A"}</span>
            </span>
            <button
              onClick={() => setShowQRModal(true)}
              className="btn btn-ghost btn-sm"
              aria-haspopup="dialog"
              aria-expanded={showQRModal}
            >
              <FaQrcode size={14} /> QR Code
            </button>
          </div>
        </motion.div>

        {/* Main grid -- capped width + centered so it doesn't stretch edge-to-edge
            on wide screens, and align-items:start so the shorter avatar card
            doesn't stretch to match the taller form column. */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-5"
          style={{ maxWidth: 1080, margin: "0 auto", alignItems: "start" }}
        >
          {/* Left: Avatar & Facts */}
          <section className="panel" style={{ padding: "var(--sp-4)" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              {/* Avatar */}
              <div style={{ position: "relative", width: 92, height: 92 }}>
                <UserAvatar
                  src={previewUrl || user.image || "/user.png"}
                  alt={user.name ? `${user.name}'s profile photo` : "Profile photo"}
                  size={92}
                  verified
                  badgeIcon={
                    <img
                      src="/club.png"
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                    />
                  }
                  ring="var(--surface-1)"
                  fallback="/user.png"
                />
                {/* Upload overlay */}
                <button
                  onClick={openFilePicker}
                  aria-label="Change profile photo"
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: "rgba(5,7,12,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity .2s var(--ease)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
                  onFocus={(e) => (e.currentTarget.style.opacity = 1)}
                  onBlur={(e) => (e.currentTarget.style.opacity = 0)}
                >
                  <span
                    style={{
                      color: "var(--text-1)",
                      fontSize: 12,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <FaCamera size={13} /> Change
                  </span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onFileInputChange}
                />
              </div>

              <h3 style={{ marginTop: "var(--sp-3)", fontSize: 15, fontWeight: 700, color: "var(--text-1)", textAlign: "center" }}>
                {user.name || "Unknown User"}
              </h3>
              <div className="eyebrow" style={{ marginTop: 2 }}>
                {user.role || "Member"}
              </div>

              <p className="field-hint" style={{ marginTop: "var(--sp-3)" }}>PNG/JPG · Max 5MB</p>

              {/* Upload / Remove */}
              <div className="grid grid-cols-2 gap-2" style={{ width: "100%", marginTop: "var(--sp-3)" }}>
                <button
                  onClick={handleImageUpload}
                  disabled={!selectedImage || uploading}
                  className="btn btn-primary btn-sm"
                  style={{ justifyContent: "center" }}
                >
                  {uploading && <span className="spinner" />}
                  {uploading ? "Uploading…" : "Upload"}
                </button>
                <button
                  onClick={() => {
                    if (previewUrl) URL.revokeObjectURL(previewUrl);
                    setSelectedImage(null);
                    setPreviewUrl(null);
                  }}
                  disabled={!selectedImage && !previewUrl}
                  className="btn btn-danger btn-sm"
                  style={{ justifyContent: "center" }}
                >
                  Remove
                </button>
              </div>

              {/* Quick facts */}
              <div style={{ width: "100%", marginTop: "var(--sp-6)", display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
                {/* ID */}
                <div style={rowStyle}>
                  <FaIdBadge style={{ color: "var(--text-2)", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>SLIIT ID</div>
                    <div className="num truncate" style={{ fontSize: 13, color: "var(--text-1)" }} title={user.SLIITID || "N/A"}>
                      {user.SLIITID || "N/A"}
                    </div>
                  </div>
                  <button onClick={copyId} className="btn btn-ghost btn-sm" aria-label="Copy SLIIT ID to clipboard">
                    Copy
                  </button>
                </div>

                {/* Mobile */}
                <div style={rowStyle}>
                  <FaPhoneAlt style={{ color: "var(--text-2)", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>Mobile</div>
                    <div className="num" style={{ fontSize: 13, color: "var(--text-1)" }}>{profile.ContactNo || "—"}</div>
                  </div>
                </div>

                {/* Address */}
                <div style={{ ...rowStyle, alignItems: "flex-start" }}>
                  <FaMapMarkerAlt style={{ marginTop: 3, color: "var(--text-2)", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>Address</div>
                    <div style={{ fontSize: 13, color: "var(--text-1)", wordBreak: "break-word" }}>{profile.Address || "—"}</div>
                  </div>
                </div>
              </div>

              {/* QR Actions */}
              <div className="grid grid-cols-3 gap-2" style={{ width: "100%", marginTop: "var(--sp-6)" }}>
                <button onClick={() => setShowQRModal(true)} className="btn btn-primary btn-sm" style={{ justifyContent: "center" }}>
                  <FaQrcode size={13} /> QR
                </button>
                <button onClick={downloadQR} className="btn btn-ghost btn-sm" style={{ justifyContent: "center" }}>
                  <FaDownload size={13} /> PNG
                </button>
                <button onClick={shareQR} className="btn btn-ghost btn-sm" style={{ justifyContent: "center" }}>
                  <FaShareAlt size={13} /> Share
                </button>
              </div>
            </div>
          </section>

          {/* Right: Forms */}
          <section className="lg:col-span-2" style={{ display: "flex", flexDirection: "column", gap: "var(--sp-6)" }}>
            {/* Personal info */}
            <div className="panel" style={{ padding: "var(--sp-4)" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "var(--sp-3)",
                  marginBottom: "var(--sp-4)",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)" }}>Personal Information</h2>
                  <p className="page-desc">Update your basic details below.</p>
                </div>
                {loadingProfile ? (
                  <span className="pill pill-info">
                    <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> Loading…
                  </span>
                ) : savingProfile ? (
                  <span style={{ fontSize: 12, color: "var(--text-3)", display: "flex", alignItems: "center", gap: 6 }}>
                    <span className="spinner" /> Saving…
                  </span>
                ) : (
                  <span className="pill pill-success">
                    <span className="dot" />
                    Synced locally
                  </span>
                )}
              </div>

              <form onSubmit={(e) => { e.preventDefault(); saveProfile(); }}>
                <div className="field">
                  <label className="field-label" htmlFor="profile-name">Name</label>
                  <input
                    id="profile-name"
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Your full name"
                  />
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="profile-mobile">Mobile</label>
                  <input
                    id="profile-mobile"
                    type="tel"
                    value={profile.ContactNo}
                    onChange={(e) => setProfile((p) => ({ ...p, ContactNo: e.target.value }))}
                    placeholder="07XXXXXXXX"
                  />
                </div>

                <div className="field" style={{ marginBottom: "var(--sp-2)" }}>
                  <label className="field-label" htmlFor="profile-address">Address</label>
                  <input
                    id="profile-address"
                    type="text"
                    value={profile.Address}
                    onChange={(e) => setProfile((p) => ({ ...p, Address: e.target.value }))}
                    placeholder="Street, City"
                  />
                </div>

                {/* Save Changes button (inside Personal Info card) */}
                <div className="flex justify-end" style={{ marginTop: "var(--sp-4)" }}>
                  <button
                    type="submit"
                    disabled={savingProfile}
                    className="btn btn-primary w-full md:w-auto"
                    style={{ justifyContent: "center" }}
                  >
                    {savingProfile && <span className="spinner" />}
                    {savingProfile ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>

            {/* Password */}
            <div className="panel" style={{ padding: "var(--sp-4)" }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)" }}>Change Password</h2>
              <p className="page-desc" style={{ marginBottom: "var(--sp-4)" }}>Create a strong, unique password.</p>

              <form onSubmit={handlePasswordSubmit}>
                <div className="field">
                  <label className="field-label" htmlFor="current-password">Current password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      id="current-password"
                      name="currentPassword"
                      type={showCurrent ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="••••••••"
                      style={{ paddingRight: 40 }}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent((s) => !s)}
                      className="icon-btn"
                      aria-label={showCurrent ? "Hide current password" : "Show current password"}
                      style={{ position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)" }}
                    >
                      {showCurrent ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                    </button>
                  </div>
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="new-password">New password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      id="new-password"
                      name="newPassword"
                      type={showNew ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      style={{ paddingRight: 40, borderColor: passwordData.newPassword ? (strong ? "var(--success)" : "var(--warning)") : undefined }}
                      placeholder="Create a strong password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((s) => !s)}
                      className="icon-btn"
                      aria-label={showNew ? "Hide new password" : "Show new password"}
                      style={{ position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)" }}
                    >
                      {showNew ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                    </button>
                  </div>
                </div>

                <div className="field" style={{ marginBottom: "var(--sp-2)" }}>
                  <label className="field-label" htmlFor="confirm-password">Confirm password</label>
                  <input
                    id="confirm-password"
                    name="confirmPassword"
                    type={showNew ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    style={{ borderColor: passwordData.confirmPassword ? (checks.match ? "var(--success)" : "var(--error)") : undefined }}
                    placeholder="Re-enter password"
                    autoComplete="new-password"
                  />
                </div>

                {/* Update Password button (inside Password card) */}
                <div
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  style={{ marginTop: "var(--sp-4)" }}
                >
                  <p className="field-hint" style={{ margin: 0 }}>Min 8 chars · Uppercase · Number · Special character</p>
                  <button
                    type="submit"
                    disabled={isUpdating || !strong || !checks.match}
                    className="btn btn-primary w-full sm:w-auto"
                    style={{ justifyContent: "center" }}
                  >
                    {isUpdating && <span className="spinner" />}
                    {isUpdating ? "Updating…" : "Update Password"}
                  </button>
                </div>

                {/* Messages */}
                {passwordSuccess && <div className="success-banner" style={{ marginTop: "var(--sp-3)" }}>{passwordSuccess}</div>}
                {passwordError && <div className="error-banner" style={{ marginTop: "var(--sp-3)" }}>{passwordError}</div>}
              </form>
            </div>
          </section>
        </motion.div>

        {/* Appearance */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          className="panel"
          style={{ maxWidth: 1080, margin: "var(--sp-6) auto 0", padding: "var(--sp-4)" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "var(--sp-3)",
              marginBottom: "var(--sp-4)",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)", display: "flex", alignItems: "center", gap: 8 }}>
                <FaPalette size={14} style={{ color: "var(--accent)" }} /> Portal Appearance
              </h2>
              <p className="page-desc">
                Choose how the portal looks. Applies instantly across the whole app and is remembered on this device.
              </p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
              gap: "var(--sp-4)",
            }}
          >
            {THEMES.map((t) => {
              const isActive = theme === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleSelectTheme(t.id)}
                  aria-pressed={isActive}
                  style={{
                    textAlign: "left",
                    background: "var(--surface-2)",
                    border: `2px solid ${isActive ? "var(--accent)" : "var(--border)"}`,
                    borderRadius: "var(--radius-md)",
                    padding: "var(--sp-3)",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    transition: "border-color .15s var(--ease), background .15s var(--ease)",
                  }}
                >
                  {/* Small visual preview */}
                  <div
                    style={{
                      height: 60,
                      borderRadius: "var(--radius-sm)",
                      overflow: "hidden",
                      display: "flex",
                      border: "1px solid var(--border)",
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    {t.swatches.map((c, i) => (
                      <span key={i} style={{ flex: 1, background: c }} />
                    ))}
                  </div>

                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-1)" }}>{t.name}</span>
                      {isActive && (
                        <span className="pill pill-success" style={{ fontSize: 10, flexShrink: 0 }}>
                          <FaCheck size={9} /> Selected
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 4, lineHeight: 1.4 }}>
                      {t.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* QR Code Modal */}
        {showQRModal &&
          createPortal(
            <div className="leo-shell-v2" style={{ minHeight: 0 }}>
            <div
              className="modal-overlay"
              onClick={() => setShowQRModal(false)}
              aria-modal="true"
              role="dialog"
              aria-labelledby="qrModalTitle"
            >
              <div className="modal-panel panel" onClick={(e) => e.stopPropagation()}>
                <div className="modal-head">
                  <h3 id="qrModalTitle">Member QR Code</h3>
                  <button onClick={() => setShowQRModal(false)} className="icon-btn" aria-label="Close">
                    <FaTimes size={16} />
                  </button>
                </div>

                <div className="modal-body" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div ref={qrBoxRef} className="panel" style={{ padding: "var(--sp-4)", background: "#0b1120" }}>
                    <QRCodeSVG
                      value={JSON.stringify({
                        SLIITID: user.SLIITID || user.id || "",
                        name: user.name || "User",
                        role: user.role || "Member",
                        contact: user.contactno || "N/A",
                      })}
                      size={220}
                      level="H"
                      includeMargin
                      bgColor="#0b1120"
                      fgColor="#e5e7eb"
                    />
                  </div>

                  <h4 style={{ marginTop: "var(--sp-4)", fontSize: 16, fontWeight: 700, color: "var(--text-1)" }}>
                    {user.name || "User"}
                  </h4>
                  <p style={{ color: "var(--text-2)", margin: "2px 0" }}>
                    ID: <span className="num">{user.SLIITID || "N/A"}</span>
                  </p>
                  <p style={{ color: "var(--text-2)", margin: "2px 0" }}>Role: {user.role || "Member"}</p>
                  {user.contactno && (
                    <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>Contact: {user.contactno}</p>
                  )}

                  <div className="grid grid-cols-3 gap-2" style={{ marginTop: "var(--sp-5)", width: "100%" }}>
                    <button onClick={downloadQR} className="btn btn-primary btn-sm" style={{ justifyContent: "center" }}>
                      <FaDownload size={13} /> Download
                    </button>
                    <button onClick={copyId} className="btn btn-ghost btn-sm" style={{ justifyContent: "center" }}>
                      <FaCopy size={13} /> Copy ID
                    </button>
                    <button onClick={shareQR} className="btn btn-ghost btn-sm" style={{ justifyContent: "center" }}>
                      <FaShareAlt size={13} /> Share
                    </button>
                  </div>
                </div>

                <div className="modal-foot">
                  <button onClick={() => setShowQRModal(false)} className="btn btn-ghost">
                    Close
                  </button>
                </div>
              </div>
            </div>
            </div>,
            document.body
          )}

        {toast && <Toast kind={toastKind} message={toast} onClose={() => setToast(null)} />}
      </main>
    </div>
  );
}
